import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';

const STORAGE_KEY = 'bo.session';

export interface StoredSession {
  accessToken: string;
  refreshToken?: string;
  sub: number;
  email: string;
  role: {
    id: number;
    role_type: string;
    permissions?: string[];
  };
}

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** Swap in a freshly minted access token, keeping the rest of the session. */
function updateAccessToken(accessToken: string): void {
  const session = loadSession();
  if (!session) return;
  saveSession({ ...session, accessToken });
}

const baseURL =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api-gateway';

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const session = loadSession();
  if (session?.accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }
  return config;
});

/**
 * Bare client for the refresh call itself — deliberately NOT `api`, so a 401
 * from the refresh endpoint can't re-enter the response interceptor below.
 */
const refreshClient = axios.create({ baseURL, withCredentials: false });

interface RefreshResponse {
  signedAccessToken?: string;
  accessToken?: string;
  data?: RefreshResponse;
}

/**
 * Single-flight refresh. Concurrent 401s all await the same in-flight promise
 * instead of firing N refresh calls (which would also invalidate each other).
 */
let refreshInFlight: Promise<string> | null = null;

function requestNewAccessToken(refreshToken: string): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = refreshClient
      .post<RefreshResponse>('/auth/refresh-token', { refreshToken })
      .then(({ data }) => {
        const payload = data?.data ?? data;
        const token = payload?.signedAccessToken ?? payload?.accessToken;
        if (!token) {
          throw new Error('Refresh succeeded but returned no access token');
        }
        updateAccessToken(token);
        return token;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function endSession(): void {
  clearSession();
  window.dispatchEvent(new CustomEvent('bo:auth-expired'));
}

// Centralised 401 handling: try one silent refresh, replay the original
// request, and only tear the session down when the refresh itself fails.
api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as
      | (InternalAxiosRequestConfig & { _boRetried?: boolean })
      | undefined;

    if (err.response?.status !== 401 || !original || original._boRetried) {
      if (err.response?.status === 401) endSession();
      return Promise.reject(err);
    }

    const refreshToken = loadSession()?.refreshToken;
    if (!refreshToken) {
      endSession();
      return Promise.reject(err);
    }

    try {
      const accessToken = await requestNewAccessToken(refreshToken);
      original._boRetried = true;
      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${accessToken}`;
      return api(original);
    } catch {
      // Refresh token expired or was revoked server-side — genuine sign-out.
      endSession();
      return Promise.reject(err);
    }
  }
);
